import { FC, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Medication as MedicationIcon,
  Healing as HealingIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  SwapHoriz as TransferIcon,
  MoveToInbox as ReceiveIcon,
  MoreVert as MoreVertIcon,
  Layers as LotsIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import EquipmentPanel from '../../components/EquipmentPanel';
import LocationsPanel from '../../components/LocationsPanel';
import TransferDialog from '../../components/TransferDialog';
import QuantityField from '../../components/QuantityField';
import { formatQuantity, roundQuantity } from '../../utils/quantity';
import DEALogsPage from '../DEALogs';
import api from '../../utils/api';
import { formatDollars } from '../../utils/money';
import { BILLING_UNIT_OPTIONS } from '../../utils/billingUnits';
import { isLowStockItem } from '../../utils/lowStock';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useRBAC } from '../../auth/useRBAC';
import { inventoryControls } from '../../auth/pageControls';
import { withOfflineFallback } from '../../offline/offlineQuery';
import { useIsOffline } from '../../offline/OfflineNotice';
import { readInventoryItems, readInventoryStats } from '../../offline/readers';
import { executeWrite, executeUpdate, executeDelete, OfflineWriteBlockedError } from '../../offline/executeWrite';
import {
  inventoryService,
  LotContainerType,
  ReceiveLotFields,
  SetInventoryPricingPayload,
  StockLocation,
} from '../../services/inventoryService';
import InventoryLotsDialog from './InventoryLotsDialog';
import { describeBalance, describeShelf } from '../../components/Vaccination/lotDraw';

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  location?: string;
  lastOrdered?: string;
  lowStockAlert?: number;
  needsPurchase?: boolean;
  // Task 9a (backend): linked ServiceCatalog.basePrice, or null when unlinked/
  // unpriced. Present on GET /inventory + GET /inventory/:id.
  catalogPrice?: number | null;
  // A lot-tracked count as whole containers plus each open bottle (ADR-0047); null or absent (offline) → units.
  lotBalance?: { containerType: LotContainerType | null; full: number; openMl: number[] } | null;
  // DEA visibility (PR5, additive; undefined on offline replica rows → no chip):
  deaMatch?: { substanceId: string; deaSchedule: string } | null;
  deaMatchState?: 'MATCHED' | 'AMBIGUOUS' | 'NONE';
  looksControlled?: boolean;
  // Task 8 (trucks): the StockLocation this row is stocked at. Each row is now
  // one product at one location — the same drug at warehouse + a truck is 2
  // rows. Falls back to the legacy free-text `location` when absent.
  locationId?: string;
  // Task 9a (backend, already shipped): the by-convention FK to the linked
  // ServiceCatalog row. Task 10 (trucks): POST /inventory/transfer resolves
  // the source row by (catalogItemId, locationId), so only catalog-linked
  // (priced) items are transferable — see TransferDialog.
  catalogItemId?: string;
  // Task 11 (trucks): per-location restock targets. `reorderPoint` feeds the
  // low-stock predicate (utils/lowStock `isLowStockItem`, the web mirror of
  // the backend's low-stock.ts: on-hand <= 0 is always low, thresholds are
  // inclusive); `parLevel` is the target the "Transfer to par" row action
  // tops the location back up to.
  reorderPoint?: number;
  parLevel?: number;
}

interface PricingFormData {
  pricingMethod: 'FIXED' | 'MARKUP';
  basePrice: string;
  unitCost: string;
  markupPercent: string;
  minPrice: string;
  unit: string;
}

const initialPricingFormData: PricingFormData = {
  pricingMethod: 'FIXED',
  basePrice: '',
  unitCost: '',
  markupPercent: '',
  minPrice: '',
  unit: '',
};

// Billable state of a row, derived from its catalog link (no column — billable
// = catalogItemId + the linked ServiceCatalog.basePrice, which GET /inventory
// attaches as `catalogPrice`). Three real states plus "unknown":
//   unknown  — catalogPrice is `undefined`: an offline replica row (they don't
//              carry catalogPrice at all); render no chip.
//   unpriced — no catalog link (or a dangling one the backend resolved to
//              null): dispensing it will NOT put a line on an invoice.
//   free     — linked at $0: it bills a $0 line ("no charge"), a deliberate
//              state, not an error.
//   priced   — linked at > $0.
type PricingState = 'unknown' | 'unpriced' | 'free' | 'priced';
const DEA_SCHEDULE_LABEL: Record<string, string> = {
  SCHEDULE_II: 'C-II', SCHEDULE_III: 'C-III', SCHEDULE_IV: 'C-IV', SCHEDULE_V: 'C-V',
};

const pricingStateOf = (item: Pick<InventoryItem, 'catalogItemId' | 'catalogPrice'>): PricingState => {
  if (item.catalogPrice === undefined) return 'unknown';
  if (!item.catalogItemId || item.catalogPrice === null) return 'unpriced';
  return item.catalogPrice > 0 ? 'priced' : 'free';
};

// Caption under the on-hand count of a low-stock row. Negative = a deficit
// placeholder (clinical dispenses may go below 0 by design — INV-37); zero =
// out of stock; otherwise name the threshold that tripped — `!= null`, never
// truthiness, so an explicit 0 threshold still prints.
const lowStockCaption = (item: InventoryItem): string => {
  if (item.quantity < 0) return `Needs ${formatQuantity(Math.abs(item.quantity))} units`;
  if (item.quantity === 0) return 'Out of stock';
  const threshold =
    item.lowStockAlert != null && item.quantity <= item.lowStockAlert ? item.lowStockAlert : item.reorderPoint;
  return `Low Stock Alert${threshold != null ? `: ${threshold} units` : ''}`;
};

interface InventoryStats {
  totalItems: number;
  totalMedications: number;
  totalSupplies: number;
  lowStockCount: number;
}

interface FormData {
  name: string;
  category: string;
  quantity: number;
  // Task 8 (trucks): the dialog's Location field is now a StockLocation
  // picker (was free text) — the create/update payload sends `locationId`;
  // the backend resolves an omitted value to the clinic's default warehouse.
  locationId?: string;
  lowStockAlert?: number;
  // Restock thresholds, per (item, location) row (ADR-0013): `reorderPoint`
  // flags the row as below-par (quantity <= it); `parLevel` is the target
  // "Transfer to par" tops the location back up to.
  reorderPoint?: number;
  parLevel?: number;
}

/** An edit sends the count only when it changed, with the on-hand it was counted from. */
type UpdateData = Omit<FormData, 'quantity'> & { quantity?: number; baseQuantity?: number };

const initialFormData: FormData = {
  name: '',
  category: 'medications',
  quantity: 0,
  locationId: undefined,
  lowStockAlert: undefined,
  reorderPoint: undefined,
  parLevel: undefined,
};

const StatCard: FC<{
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
}> = ({ title, value, subtitle, icon }) => (
  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: 'none', borderRadius: 3 }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box minWidth={0} flex={1}>
          <Typography 
            sx={{ 
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h4"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 700,
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem', mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ 
          bgcolor: 'rgba(139,94,52,0.08)', 
          borderRadius: '50%', 
          width: 48, 
          height: 48, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'primary.main',
          flexShrink: 0,
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Inventory: FC = () => {
  const { trackFeatureUsed } = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  // Deep link: /inventory?tab=equipment (calendar equipment strip's Manage link),
  // /inventory?tab=dea-logs (relocated DEA Logs), /inventory?tab=locations
  // (Task 9, trucks: manage StockLocations).
  const initialTab = ['all', 'medications', 'supplies', 'equipment', 'dea-logs', 'locations'].includes(searchParams.get('tab') ?? '')
    ? (searchParams.get('tab') as string)
    : 'all';
  const [categoryFilter, setCategoryFilter] = useState<string>(initialTab);
  // "Not DEA-tracked" chip → jump to the DEA Logs tab with the Add Substance
  // dialog prefilled to this item's name (PR5).
  const [enrolName, setEnrolName] = useState<string | null>(null);
  const [showLowStock, setShowLowStock] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Task 8 (trucks): location is orthogonal to category — a separate dropdown,
  // not another Tab. "all" = no location filter (one row per (item, location)).
  const [locationFilter, setLocationFilter] = useState<string>('all');

  // Task 9b: "add to price list?" prompt shown after a save, gated on
  // manage:inventory_pricing + online (PUT /inventory/:id/pricing — the P3 sweep
  // re-pointed it off the coarse `manage:billing`; a direct api.put, never the
  // offline queue).
  const rbac = useRBAC();
  const isOffline = useIsOffline();
  // `InventoryItem.lastOrdered` is stamped `new Date()` by receiveStock — an
  // INSTANT (DATE-KINDS §1.2: the client-supplied DTO arm is dormant), so the
  // "Last received" caption renders in the CLINIC'S zone.
  const formatInstant = useFormatInstant();
  // VERIFIED against inventory.controller.ts `updatePricing` :154-155 - manage:inventory_pricing
  // is exactly what PUT /inventory/:id/pricing declares, and the slug is now spelled once, in
  // `auth/pageControls.ts`, so a re-pointed gate moves both halves together (INV-108).
  const can = inventoryControls(rbac);
  const canPromptPricing = can.pricing && !isOffline;
  const [pricingPromptItem, setPricingPromptItem] = useState<{ id: string; name: string } | null>(null);
  const [pricingFormData, setPricingFormData] = useState<PricingFormData>(initialPricingFormData);

  // Task 10 (trucks): "Transfer stock" is a manager action, online-only
  // (direct api via inventoryService.transfer, never the offline queue) —
  // same gate as Task 9's Locations tab writes.
  // VERIFIED against inventory.controller.ts `transfer` :68-69 - manage:inventory.
  const canTransfer = can.transfer && !isOffline;
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  // Task 11 (trucks): pre-fill for the "Transfer to par" row action — reuses
  // the same TransferDialog as the header's manual "Transfer stock" button,
  // just opened with To/Item/Quantity already set to close a below-par
  // shortfall. Cleared on close so the header button keeps Task 10's plain
  // (empty) defaults.
  const [transferPrefill, setTransferPrefill] = useState<{
    toLocationId: string;
    catalogItemId: string;
    quantity: number;
  } | null>(null);

  // Receive stock (2026-08-21): explicit restock — online-only, manage:inventory
  // (same gate as Transfer; StockMovement is never client-pushed, INV-40). The
  // idempotencyKey is minted when the dialog OPENS so a double-click on
  // "Receive" replays the same movement instead of double-applying it.
  const [receiveItem, setReceiveItem] = useState<{ id: string; name: string } | null>(null);
  // The row whose action menu is open, and the item whose lots are on screen.
  const [rowMenu, setRowMenu] = useState<{ id: string; el: HTMLElement } | null>(null);
  const [lotsItem, setLotsItem] = useState<{ id: string; name: string } | null>(null);
  const [receiveQty, setReceiveQty] = useState<number | null>(null);
  const [receiveReason, setReceiveReason] = useState('');
  const [receiveKey, setReceiveKey] = useState('');
  const openReceiveDialog = (item: { id: string; name: string }) => {
    setReceiveItem(item);
    setReceiveQty(null);
    setReceiveReason('');
    setReceiveLot({});
    setReceiveKey(
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${item.id}`,
    );
  };
  // The lot this delivery came in (ADR-0047). Optional; a new lot number needs its expiry and packaging,
  // which start from the item's last lot because a product is bought the same way every time.
  const [receiveLot, setReceiveLot] = useState<ReceiveLotFields>({});
  const { data: receiveItemLots = [] } = useQuery(
    ['inventory', receiveItem?.id, 'lots'],
    () => inventoryService.getItemLots(receiveItem!.id),
    { enabled: !!receiveItem, retry: false },
  );
  const lastLot = useMemo(
    () => [...receiveItemLots].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [receiveItemLots],
  );
  const typedLotNumber = (receiveLot.lotNumber ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  const knownLot = typedLotNumber ? receiveItemLots.find((l) => l.lotNumber === typedLotNumber) : undefined;
  const receivePackaging = knownLot
    ? { containerType: knownLot.containerType, mlPerContainer: knownLot.mlPerContainer }
    : {
        containerType: receiveLot.containerType ?? lastLot?.containerType ?? 'MULTI_DOSE',
        mlPerContainer: receiveLot.mlPerContainer ?? lastLot?.mlPerContainer,
      };
  const receiveMutation = useMutation(
    () => {
      if (!receiveItem) return Promise.reject(new Error('No item selected'));
      return inventoryService.receive(receiveItem.id, {
        quantity: receiveQty ?? 0,
        reason: receiveReason.trim() || undefined,
        idempotencyKey: receiveKey,
        ...(typedLotNumber
          ? knownLot
            ? { lotNumber: typedLotNumber, manufacturer: receiveLot.manufacturer?.trim() || undefined }
            : {
                lotNumber: typedLotNumber,
                expiresOn: receiveLot.expiresOn,
                containerType: receivePackaging.containerType,
                mlPerContainer: receivePackaging.mlPerContainer,
                manufacturer: receiveLot.manufacturer?.trim() || undefined,
                inUseDays: receiveLot.inUseDays ?? lastLot?.inUseDays ?? undefined,
              }
          : {}),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        enqueueSnackbar('Stock received', { variant: 'success' });
        setReceiveItem(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to receive stock', { variant: 'error' });
      },
    }
  );
  const newLotIncomplete =
    !!typedLotNumber && !knownLot && (!receiveLot.expiresOn || !receivePackaging.mlPerContainer || receivePackaging.mlPerContainer <= 0);
  const isReceiveValid = receiveQty !== null && receiveQty > 0 && !newLotIncomplete;

  const handleTransferToPar = (item: InventoryItem) => {
    if (!item.catalogItemId || item.parLevel == null) return;
    setTransferPrefill({
      toLocationId: item.locationId || '',
      catalogItemId: item.catalogItemId,
      // The shortfall, in the hundredths people can enter.
      quantity: Math.max(0.01, roundQuantity(item.parLevel - item.quantity, 2)),
    });
    setTransferDialogOpen(true);
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferPrefill(null);
  };

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: items = [], isLoading, isError: itemsError } = useQuery<InventoryItem[]>(
    ['inventory'],
    withOfflineFallback(
      async () => {
        const response = await api.get('/inventory');
        return response.data;
      },
      // Replica fallback: return the full list (filtering happens in-memory via
      // `filteredItems`), matching the unfiltered online fetch shape (array).
      () => readInventoryItems() as Promise<InventoryItem[]>,
    )
  );

  // Task 8 (trucks): StockLocations for the filter dropdown + Add/Edit dialog
  // picker + row location-name resolution. Offline-aware at the service layer
  // (reads the synced replica when offline) — no withOfflineFallback needed here.
  const { data: locations = [] } = useQuery<StockLocation[]>(
    ['stock-locations'],
    () => inventoryService.getLocations(),
  );

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((loc) => map.set(loc.id, loc.name));
    return map;
  }, [locations]);

  const defaultLocationId = useMemo(() => locations.find((loc) => loc.isDefault)?.id, [locations]);

  const { data: stats = { totalItems: 0, totalMedications: 0, totalSupplies: 0, lowStockCount: 0 } } = useQuery<InventoryStats>(
    ['inventory-stats'],
    withOfflineFallback(
      async () => {
        const response = await api.get('/inventory/stats');
        return response.data;
      },
      // Adapt the replica stats shape ({ total, lowStock, byCategory }) to the
      // InventoryStats shape the component consumes.
      async () => {
        const s = await readInventoryStats();
        return {
          totalItems: s.total,
          totalMedications: s.byCategory?.medications ?? 0,
          totalSupplies: s.byCategory?.supplies ?? 0,
          lowStockCount: s.lowStock,
        };
      },
    )
  );

  const createItem = useMutation(
    async (data: FormData) => {
      return executeWrite({
        entityType: 'inventoryItem',
        table: 'inventoryItems',
        payload: data,
        networkFn: () => api.post('/inventory', data).then((r) => r.data),
      });
    },
    {
      onSuccess: (data: any) => {
        trackFeatureUsed('inventory_item_created');
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        handleCloseDialog();
        enqueueSnackbar('Item created', { variant: 'success' });
        // A newly created item has no catalog link yet — always a candidate
        // for the pricing prompt (create's API response doesn't carry
        // catalogPrice, so treat missing as unpriced, which it always is here).
        offerPricingPrompt({
          id: data.id,
          name: data.name,
          catalogItemId: data.catalogItemId ?? undefined,
          catalogPrice: data.catalogPrice ?? null,
        });
      },
      onError: (error: any, variables: any) => {
        if (error instanceof OfflineWriteBlockedError) {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        // Duplicate identity (409): offer to RECEIVE the attempted quantity
        // into the existing row instead of showing a dead-end error.
        const body = error.response?.data;
        if (error.response?.status === 409 && body?.code === 'INVENTORY_DUPLICATE' && body?.details?.existingId) {
          handleCloseDialog();
          setDupPrompt({
            existingId: body.details?.existingId,
            existingQuantity: body.details?.existingQuantity ?? 0,
            name: variables?.name ?? '',
            attemptedQty: typeof variables?.quantity === 'number' ? variables.quantity : 0,
          });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to create item', { variant: 'error' });
      },
    }
  );

  // Duplicate-create prompt (2026-08-24): the server refused a second row for
  // the same (name, location); receiving into the survivor is the right verb.
  const [dupPrompt, setDupPrompt] = useState<{ existingId: string; existingQuantity: number; name: string; attemptedQty: number } | null>(null);
  const dupReceiveMutation = useMutation(
    () => {
      if (!dupPrompt) return Promise.reject(new Error('No duplicate selected'));
      return inventoryService.receive(dupPrompt.existingId, {
        quantity: dupPrompt.attemptedQty,
        reason: 'received via duplicate-create prompt',
        idempotencyKey:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `dup-${Date.now()}`,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        enqueueSnackbar('Stock received into the existing item', { variant: 'success' });
        setDupPrompt(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to receive stock', { variant: 'error' });
      },
    }
  );

  const updateItem = useMutation(
    async ({ id, data }: { id: string; data: UpdateData }) => {
      return executeUpdate({
        entityType: 'inventoryItem',
        table: 'inventoryItems',
        entityId: id,
        payload: data,
        networkFn: () => api.put(`/inventory/${id}`, data).then((r) => r.data),
      });
    },
    {
      onSuccess: (_data: any, variables) => {
        trackFeatureUsed('inventory_item_updated');
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        handleCloseDialog();
        enqueueSnackbar('Item updated', { variant: 'success' });
        // The update response doesn't carry catalogPrice either (name/qty/etc
        // edits don't touch pricing) — use the pre-edit list row, which already
        // has catalogPrice from the last GET, to decide if it's still unpriced.
        const priorItem = items.find((i) => i.id === variables.id);
        offerPricingPrompt({
          id: variables.id,
          name: variables.data.name,
          catalogItemId: priorItem?.catalogItemId,
          catalogPrice: priorItem?.catalogPrice ?? null,
        });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError) {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to update item', { variant: 'error' });
      },
    }
  );

  // Gated at the call site's data (canPromptPricing covers the RBAC + online
  // check); shows the "add to price list?" dialog only for an UNPRICED item
  // (no catalog link). A row deliberately linked at $0 ("no charge") is on the
  // price list already — its row chip says so; don't nag on every edit.
  const offerPricingPrompt = (item: { id: string; name: string; catalogItemId?: string; catalogPrice?: number | null }) => {
    if (!canPromptPricing) return;
    if (pricingStateOf(item) !== 'unpriced') return;
    setPricingFormData(initialPricingFormData);
    setPricingPromptItem({ id: item.id, name: item.name });
  };

  const computePricingMarkupPreview = (): number | null => {
    const cost = parseFloat(pricingFormData.unitCost);
    const markup = parseFloat(pricingFormData.markupPercent);
    if (isNaN(cost) || isNaN(markup)) return null;
    const parsedMin = pricingFormData.minPrice !== '' ? parseFloat(pricingFormData.minPrice) : 0;
    const min = isNaN(parsedMin) ? 0 : parsedMin;
    const raw = cost * (1 + markup / 100);
    const floored = Math.max(raw, min);
    return Math.round(floored * 100) / 100;
  };

  const setPricingMutation = useMutation(
    (payload: SetInventoryPricingPayload) => {
      if (!pricingPromptItem) return Promise.reject(new Error('No item selected'));
      return inventoryService.setPricing(pricingPromptItem.id, payload);
    },
    {
      onSuccess: (data: any, payload) => {
        trackFeatureUsed('inventory_item_priced');
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        // Honest toast: the dialog allows $0 (min 0), so say what a $0 link
        // means — the item is on the price list and bills a $0 (no-charge) line. Prefer the
        // server's resolved basePrice (setPricing returns the linked catalog
        // row); fall back to what the form sent.
        const linkedPrice: number | null =
          typeof data?.catalogItem?.basePrice === 'number'
            ? data.catalogItem.basePrice
            : payload.pricingMethod === 'MARKUP'
              ? computePricingMarkupPreview()
              : payload.basePrice ?? null;
        if (linkedPrice !== null && linkedPrice <= 0) {
          enqueueSnackbar('Linked at $0 — items will post no-charge lines on invoices', { variant: 'warning' });
        } else {
          enqueueSnackbar('Price added — this item can now be billed', { variant: 'success' });
        }
        setPricingPromptItem(null);
        setPricingFormData(initialPricingFormData);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to set price', { variant: 'error' });
      },
    }
  );

  const isPricingMarkupMethod = pricingFormData.pricingMethod === 'MARKUP';
  const pricingMarkupPreview = computePricingMarkupPreview();
  // The dialog's design allows a $0 price (min 0) — say up front what that
  // means instead of refusing it (the success toast repeats it).
  const pricingPreviewIsZero = isPricingMarkupMethod
    ? pricingMarkupPreview === 0
    : pricingFormData.basePrice !== '' && parseFloat(pricingFormData.basePrice) === 0;
  const isPricingValid = isPricingMarkupMethod
    ? (
        pricingFormData.unitCost !== '' && !isNaN(parseFloat(pricingFormData.unitCost)) && parseFloat(pricingFormData.unitCost) >= 0 &&
        pricingFormData.markupPercent !== '' && !isNaN(parseFloat(pricingFormData.markupPercent)) && parseFloat(pricingFormData.markupPercent) >= 0
      )
    : (pricingFormData.basePrice !== '' && !isNaN(parseFloat(pricingFormData.basePrice)) && parseFloat(pricingFormData.basePrice) >= 0);

  const handleClosePricingPrompt = () => {
    setPricingPromptItem(null);
    setPricingFormData(initialPricingFormData);
  };

  const handleConfirmPricing = () => {
    const payload: SetInventoryPricingPayload = { pricingMethod: pricingFormData.pricingMethod };
    if (isPricingMarkupMethod) {
      payload.unitCost = parseFloat(pricingFormData.unitCost);
      payload.markupPercent = parseFloat(pricingFormData.markupPercent);
      if (pricingFormData.minPrice !== '') {
        payload.minPrice = parseFloat(pricingFormData.minPrice);
      }
    } else {
      payload.basePrice = parseFloat(pricingFormData.basePrice) || 0;
    }
    // Free text, so trim and send only when the user actually chose one — an
    // empty string would overwrite a unit already on a linked catalog row.
    const unit = pricingFormData.unit.trim();
    if (unit) payload.unit = unit;
    setPricingMutation.mutate(payload);
  };

  const deleteItem = useMutation(
    async (id: string) => {
      await executeDelete({
        entityType: 'inventoryItem',
        table: 'inventoryItems',
        entityId: id,
        networkFn: () => api.delete(`/inventory/${id}`).then((r) => r.data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['inventory-stats']);
        enqueueSnackbar('Item deleted', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError) {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete item', { variant: 'error' });
      },
    }
  );

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = searchTerm === '' ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || item.category.toLowerCase() === categoryFilter.toLowerCase();

      // "all" = no location filter (shows one row per (item, location)).
      const matchesLocation = locationFilter === 'all' || item.locationId === locationFilter;

      // ONE predicate (utils/lowStock mirrors the backend's low-stock.ts):
      // on-hand <= 0 is always low; lowStockAlert / per-location reorderPoint
      // are inclusive thresholds; the persisted needsPurchase flag counts too.
      const matchesLowStock = !showLowStock || isLowStockItem(item);

      return matchesSearch && matchesCategory && matchesLocation && matchesLowStock;
    });
  }, [items, searchTerm, categoryFilter, locationFilter, showLowStock]);

  const handleOpenDialog = (item?: InventoryItem) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        category: item.category,
        quantity: item.quantity,
        locationId: item.locationId ?? defaultLocationId,
        lowStockAlert: item.lowStockAlert,
        reorderPoint: item.reorderPoint ?? undefined,
        parLevel: item.parLevel ?? undefined,
      });
    } else {
      setSelectedItem(null);
      setFormData({ ...initialFormData, locationId: defaultLocationId });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedItem(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (selectedItem) {
      // A count goes only when it changed, with the on-hand it was counted from (baseQuantity): the server
      // books the difference as a COUNT movement, and an untouched box must not post one (ADR-0038).
      const { quantity, ...rest } = formData;
      const data: UpdateData =
        quantity === selectedItem.quantity ? rest : { ...rest, quantity, baseQuantity: selectedItem.quantity };
      updateItem.mutate({ id: selectedItem.id, data });
    } else {
      createItem.mutate(formData);
    }
  };

  // Task 8 (trucks): the stat cards (['inventory-stats']) are clinic-wide —
  // scoping them per-location is deferred (v1 relabels instead, so a selected
  // location's numbers never read as if they were location-scoped).
  const locationScopeNote = locationFilter !== 'all' ? ' (All Locations)' : '';

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteItem.mutate(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  if (isLoading) {
    return (
      <Box p={2}>
        <Typography>Loading inventory...</Typography>
      </Box>
    );
  }

  if (itemsError) {
    return (
      <Box p={2}>
        <Alert severity="error">Failed to load inventory. Please try refreshing the page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'hidden', p: { xs: 1, sm: 1.5 } }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={{ xs: 2, sm: 3 }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1.5, sm: 0 }}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, fontWeight: 700, mb: 0.5 }}>Inventory</Typography>
          <Typography color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Manage medications, supplies, and equipment
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          {/* Transfer keeps its DISABLED arm, because the reason it is unavailable is usually
              being offline rather than being refused; the permission half now hides it outright. */}
          {can.transfer && (
            <Button
              variant="outlined"
              onClick={() => setTransferDialogOpen(true)}
              disabled={!canTransfer}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Transfer stock
            </Button>
          )}
          {can.add && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                borderRadius: 2,
                bgcolor: '#3B2E1A',
                '&:hover': { bgcolor: '#2A2012' },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Add Item
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} mb={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={`Total Items${locationScopeNote}`}
            value={stats.totalItems}
            subtitle="Across all categories"
            icon={<InventoryIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={`Medications${locationScopeNote}`}
            value={stats.totalMedications}
            subtitle={`${items.filter(i => i.category === 'medications' && isLowStockItem(i)).length} low stock`}
            icon={<MedicationIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={`Supplies${locationScopeNote}`}
            value={stats.totalSupplies}
            subtitle={`${items.filter(i => i.category === 'supplies' && isLowStockItem(i)).length} low stock`}
            icon={<HealingIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: '1px solid',
              borderColor: showLowStock ? 'primary.main' : 'divider',
              boxShadow: 'none',
              borderRadius: 3,
              bgcolor: showLowStock ? 'primary.main' : 'background.paper',
              color: showLowStock ? 'primary.contrastText' : 'text.primary',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => setShowLowStock(!showLowStock)}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 600, 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase',
                    color: showLowStock ? 'inherit' : 'text.secondary', 
                    mb: 0.5,
                  }}>
                    Low Stock Alerts{locationScopeNote}
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700 }}>
                    {stats.lowStockCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5, color: showLowStock ? 'inherit' : 'text.secondary' }}>
                    Items need reordering
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: showLowStock ? 'rgba(255,255,255,0.2)' : 'rgba(139,94,52,0.08)', 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: showLowStock ? 'inherit' : 'primary.main',
                  flexShrink: 0,
                }}>
                  <WarningIcon />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        <Box p={{ xs: 1.5, sm: 2.5 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search inventory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                minWidth: 240,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              select
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            >
              <MenuItem value="all">All locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
              ))}
            </TextField>
            <Button
              variant={showLowStock ? "contained" : "outlined"}
              color="primary"
              onClick={() => setShowLowStock(!showLowStock)}
              startIcon={<WarningIcon />}
              sx={{ borderRadius: 2 }}
            >
              {showLowStock ? "Show All" : "Show Low Stock"}
            </Button>
          </Box>
        </Box>
        <Tabs
          value={categoryFilter}
          onChange={(_, value) => {
            // Leaving the DEA tab abandons any pending enrol request — otherwise
            // a stale enrolName re-opens the Add dialog on the next visit.
            if (value !== 'dea-logs') setEnrolName(null);
            setCategoryFilter(value);
          }}
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', color: 'text.secondary', '&.Mui-selected': { color: '#C9772B', fontWeight: 600 } },
            '& .MuiTabs-indicator': { backgroundColor: '#C9772B' },
          }}
        >
          <Tab label="All Items" value="all" />
          <Tab label="Medications" value="medications" />
          <Tab label="Supplies" value="supplies" />
          <Tab label="Equipment" value="equipment" />
          <Tab label="Locations" value="locations" />
          <Tab
            value="dea-logs"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                DEA Logs
                <Chip
                  label="Beta"
                  size="small"
                  sx={{
                    height: 18, fontSize: '0.6rem', fontWeight: 600,
                    bgcolor: '#C9772B', color: '#fff', '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              </Box>
            }
          />
        </Tabs>

        <Box p={{ xs: 1.5, sm: 2.5 }}>
          {/* Schedulable units + day assignments live here (no separate page);
              the list below stays the stock-tracking view. */}
          {categoryFilter === 'equipment' && <EquipmentPanel />}
          {categoryFilter === 'locations' && <LocationsPanel />}
          {categoryFilter === 'dea-logs' && (
            <DEALogsPage enrolName={enrolName} onEnrolConsumed={() => setEnrolName(null)} />
          )}
          {filteredItems.length === 0 && categoryFilter !== 'equipment' && categoryFilter !== 'locations' && categoryFilter !== 'dea-logs' && (
            <Box py={6} textAlign="center">
              <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No items found</Typography>
            </Box>
          )}
          {categoryFilter === 'equipment' && filteredItems.length > 0 && (
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
              Equipment Stock Items
            </Typography>
          )}
          {filteredItems.map((item) => (
            <Box
              key={item.id}
              p={2}
              mb={1.5}
              border="1px solid"
              borderColor="divider"
              borderRadius={2}
              sx={{ 
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: 'primary.light' },
                '&:last-child': { mb: 0 },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box minWidth={0} flex={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                    {/* 3-state billable chip (no column): unpriced = no catalog
                        link, dispensing won't bill; free = linked at $0, bills
                        a no-charge line; priced = $X. No chip for 'unknown'.
                        TODO: offline replica rows lack catalogPrice — a future
                        enhancement could join the serviceCatalog replica in
                        readInventoryItems to attach it offline. */}
                    {(() => {
                      const pricingState = pricingStateOf(item);
                      if (pricingState === 'unknown') return null;
                      if (pricingState === 'unpriced') {
                        return (
                          <Chip
                            label="Not priced — won't bill"
                            title={canPromptPricing ? 'Add this item to your price list' : undefined}
                            size="small"
                            color="warning"
                            variant="outlined"
                            // The pricing prompt used to appear once after a
                            // save and could never be reopened, so an item
                            // skipped at creation was unpriceable from this
                            // screen. Same affordance as the DEA chip below.
                            onClick={canPromptPricing ? () => offerPricingPrompt(item) : undefined}
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        );
                      }
                      if (pricingState === 'free') {
                        return (
                          <Chip
                            label="$0 — no charge"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        );
                      }
                      return (
                        <Chip
                          label={formatDollars(item.catalogPrice as number)}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      );
                    })()}
                    {/* DEA visibility (PR5): MATCHED → schedule chip; ambiguous
                        registry names or curated-controlled-but-unenrolled →
                        warning (the auto-log SKIPS those dispenses). undefined
                        = offline replica → no chip, like catalogPrice. */}
                    {item.deaMatchState === undefined ? null : item.deaMatchState === 'MATCHED' && item.deaMatch ? (
                      <Chip
                        label={`DEA ${DEA_SCHEDULE_LABEL[item.deaMatch.deaSchedule] ?? item.deaMatch.deaSchedule}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ) : item.deaMatchState === 'AMBIGUOUS' ? (
                      <Chip
                        label="DEA match ambiguous"
                        title="Two or more registry entries share this name — dispenses of this item are NOT auto-logged. Disambiguate under DEA Logs → Substances & Lots."
                        size="small"
                        color="warning"
                        variant="outlined"
                        // Jump only — NEVER prefill the Add dialog here: a third
                        // same-name registry row would deepen the ambiguity.
                        onClick={() => setCategoryFilter('dea-logs')}
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ) : item.looksControlled ? (
                      <Chip
                        label="Not DEA-tracked"
                        title="This looks like a controlled substance but isn't in your DEA registry — dispenses are NOT logged to the DEA ledger. Click to enrol it."
                        size="small"
                        color="warning"
                        onClick={() => { setEnrolName(item.name); setCategoryFilter('dea-logs'); }}
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ) : null}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {/* Task 8 (trucks): resolve the StockLocation NAME from locationId;
                        fall back to the legacy free-text `location` when no locationId
                        (older rows / offline replica gaps). */}
                    Location: {(item.locationId && locationNameById.get(item.locationId)) || item.location || 'Not specified'} &bull; Last received: <span data-testid="inventory-last-received">{item.lastOrdered ? formatInstant(item.lastOrdered, INSTANT_FORMATS.dateNumeric) : 'Never'}</span>
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box textAlign="right">
                    <Typography sx={{ fontWeight: 600 }}>
                      {item.lotBalance ? describeShelf(item.lotBalance) : `${formatQuantity(item.quantity)} units`}
                    </Typography>
                    {isLowStockItem(item) && (
                      <Typography variant="caption" color="error.main" sx={{ fontWeight: 500 }}>
                        {lowStockCaption(item)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Receive stock (2026-08-21): the explicit restock verb —
                        writes a RECEIVE ledger row and stamps "Last received".
                        Online-only like Transfer (INV-40). */}
                    {can.receive && (
                      <IconButton
                        size="small"
                        onClick={() => openReceiveDialog({ id: item.id, name: item.name })}
                        disabled={isOffline}
                        title="Receive stock"
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <ReceiveIcon fontSize="small" />
                      </IconButton>
                    )}
                    {/* Task 11 (trucks): "Transfer to par" — only when this
                        location row has a real below-par shortfall against a
                        configured parLevel AND is catalog-linked (the
                        transfer endpoint resolves the FROM row by
                        catalogItemId). Same gate as the header's Transfer
                        stock button (manage:inventory + online). */}
                    {/* One visible action (receiving is done many rows in a row) and the rest named in
                        a menu, the shape of the other lists in the app. Lots are readable by anyone
                        on this page (GET /inventory/:id/lots is view:inventory). */}
                    <IconButton
                      size="small"
                      onClick={(e) => setRowMenu({ id: item.id, el: e.currentTarget })}
                      title="More actions"
                      aria-label={`More actions for ${item.name}`}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu open={rowMenu?.id === item.id} anchorEl={rowMenu?.el ?? null} onClose={() => setRowMenu(null)}>
                      <MenuItem
                        onClick={() => {
                          setLotsItem({ id: item.id, name: item.name });
                          setRowMenu(null);
                        }}
                      >
                        <ListItemIcon><LotsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Lots and expiry dates</ListItemText>
                      </MenuItem>
                      {can.transfer && item.parLevel != null && item.parLevel > item.quantity && item.catalogItemId && (
                        <MenuItem
                          disabled={!canTransfer}
                          onClick={() => {
                            handleTransferToPar(item);
                            setRowMenu(null);
                          }}
                        >
                          <ListItemIcon><TransferIcon fontSize="small" /></ListItemIcon>
                          <ListItemText>Transfer to par</ListItemText>
                        </MenuItem>
                      )}
                      {can.edit && (
                        <MenuItem
                          onClick={() => {
                            handleOpenDialog(item);
                            setRowMenu(null);
                          }}
                        >
                          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                          <ListItemText>Edit details</ListItemText>
                        </MenuItem>
                      )}
                      {can.remove && <Divider />}
                      {can.remove && (
                        <MenuItem
                          onClick={() => {
                            handleDelete(item.id);
                            setRowMenu(null);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                          <ListItemText>Delete item</ListItemText>
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              select
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="medications">Medications</MenuItem>
              <MenuItem value="supplies">Supplies</MenuItem>
              <MenuItem value="equipment">Equipment</MenuItem>
            </TextField>

            <QuantityField
              label="Quantity"
              value={formData.quantity}
              onChange={(next) => setFormData({ ...formData, quantity: next ?? 0 })}
              fullWidth
              required
            />

            <TextField
              select
              label="Location"
              value={formData.locationId || ''}
              onChange={(e) => setFormData({ ...formData, locationId: e.target.value || undefined })}
              fullWidth
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
              ))}
            </TextField>

            {/* `?? ''` / explicit-empty check (not `||`): an explicit 0
                threshold is a real value ("alert the moment it hits zero"),
                and the low-stock predicate treats it as such. */}
            <TextField
              label="Low Stock Alert"
              type="number"
              value={formData.lowStockAlert ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lowStockAlert: e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value) || 0),
                })
              }
              fullWidth
              placeholder="Minimum quantity before alert"
              helperText="Flag as low stock at or under this quantity (zero on hand is always flagged)"
            />

            {/* Restock thresholds (per location): reorderPoint drives the
                below-par flag; parLevel is what "Transfer to par" tops the
                location back up to. Side by side — they're read together. */}
            <Box display="flex" gap={2}>
              <TextField
                label="Reorder point"
                type="number"
                value={formData.reorderPoint ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    reorderPoint: e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value) || 0),
                  })
                }
                fullWidth
                helperText="Flag as below par at or under this quantity"
              />
              <TextField
                label="Par level"
                type="number"
                value={formData.parLevel ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    parLevel: e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value) || 0),
                  })
                }
                fullWidth
                helperText="Restock target — “Transfer to par” fills up to this"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || !formData.category || formData.quantity < 0}
          >
            {selectedItem ? 'Update' : 'Add'} Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Item?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this inventory item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to price list? prompt (Task 9b) — shown after saving a not-priced
          item, only for manage:inventory_pricing users while online (offerPricingPrompt
          gates it; see canPromptPricing). Skipping just closes it — the
          not-priced chip on the row is the persistent reminder. */}
      <Dialog open={!!pricingPromptItem} onClose={handleClosePricingPrompt} maxWidth="xs" fullWidth>
        <DialogTitle>Add to price list?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Add &quot;{pricingPromptItem?.name}&quot; to your price list so it can be billed?
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <ToggleButtonGroup
              value={pricingFormData.pricingMethod}
              exclusive
              size="small"
              onChange={(_, value) => {
                if (value) setPricingFormData({ ...pricingFormData, pricingMethod: value });
              }}
            >
              <ToggleButton value="FIXED" sx={{ textTransform: 'none' }}>
                Fixed price
              </ToggleButton>
              <ToggleButton value="MARKUP" sx={{ textTransform: 'none' }}>
                Use cost + markup
              </ToggleButton>
            </ToggleButtonGroup>

            {isPricingMarkupMethod && (
              <>
                <TextField
                  label="Unit Cost"
                  type="number"
                  value={pricingFormData.unitCost}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, unitCost: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="What this item costs your clinic per sell unit"
                />
                <TextField
                  label="Markup %"
                  type="number"
                  value={pricingFormData.markupPercent}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, markupPercent: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="e.g., 100 = sell at double the cost"
                />
                <TextField
                  label="Minimum price (optional)"
                  type="number"
                  value={pricingFormData.minPrice}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, minPrice: e.target.value })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Never sell below this, whatever the markup works out to"
                />
              </>
            )}

            <TextField
              label="Price"
              type="number"
              value={isPricingMarkupMethod ? (pricingMarkupPreview !== null ? pricingMarkupPreview.toFixed(2) : '') : pricingFormData.basePrice}
              onChange={(e) => setPricingFormData({ ...pricingFormData, basePrice: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                readOnly: isPricingMarkupMethod,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={
                pricingPreviewIsZero
                  ? '$0 — on the price list; billed lines post at no charge'
                  : isPricingMarkupMethod
                    ? 'Derived from cost + markup — read-only'
                    : undefined
              }
              FormHelperTextProps={pricingPreviewIsZero ? { sx: { color: 'warning.main' } } : undefined}
            />
            {/* What the price is quoted per. Set here rather than only in
                Settings because this is the moment the stock physically
                arrives and someone knows whether it sells by the bag or by
                the mL. freeSolo: the column is free text and live clinics
                already hold values outside the list. */}
            <Autocomplete
              freeSolo
              options={BILLING_UNIT_OPTIONS}
              value={pricingFormData.unit}
              onInputChange={(_, value) => setPricingFormData({ ...pricingFormData, unit: value })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Priced per (optional)"
                  placeholder="e.g. per dose, per mL, per bag"
                  helperText="What one unit of this price covers"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePricingPrompt}>Skip</Button>
          <Button
            onClick={handleConfirmPricing}
            variant="contained"
            color="primary"
            disabled={!isPricingValid || setPricingMutation.isLoading}
          >
            Add to Price List
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate-create prompt: same (name, location) already exists. */}
      <Dialog open={!!dupPrompt} onClose={() => setDupPrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Item already exists</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {dupPrompt ? `"${dupPrompt.name}" already exists at this location with ${formatQuantity(dupPrompt.existingQuantity)} on hand. Receive ${formatQuantity(dupPrompt.attemptedQty)} into it instead of creating a duplicate row?` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupPrompt(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!dupPrompt || !(dupPrompt.attemptedQty > 0) || dupReceiveMutation.isLoading}
            onClick={() => dupReceiveMutation.mutate()}
          >
            {dupPrompt && dupPrompt.attemptedQty > 0 ? `Receive ${formatQuantity(dupPrompt.attemptedQty)} into existing` : 'Nothing to receive'}
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryLotsDialog
        item={lotsItem}
        onClose={() => setLotsItem(null)}
        canEdit={can.editLot && !isOffline}
        canRecount={can.recountLot && !isOffline}
        canAddDetails={can.addLotDetails && !isOffline}
      />

      {/* Receive stock (2026-08-21) — explicit restock; posts a RECEIVE ledger
          row and stamps "Last received". */}
      <Dialog open={!!receiveItem} onClose={() => setReceiveItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive stock{receiveItem ? ` — ${receiveItem.name}` : ''}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <QuantityField
              label="Containers received"
              helperText="Vials or bottles, not doses"
              value={receiveQty}
              onChange={(next) => {
                setReceiveQty(next);
                // A changed quantity is a NEW intent — mint a fresh key so a
                // retry after editing cannot silently replay the old amount.
                setReceiveKey(
                  typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${next}`,
                );
              }}
              autoFocus
              fullWidth
              required
            />
            <TextField
              label="Note (optional)"
              value={receiveReason}
              onChange={(e) => setReceiveReason(e.target.value)}
              placeholder="e.g. PO #1234, Covetrus order"
              fullWidth
            />

            {/* The lot this delivery came in (spec §8, §10). Optional, but a lot skipped here is typed by
                hand into every vaccination it goes into. */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Lot (optional)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Lot number"
                    size="small"
                    fullWidth
                    value={receiveLot.lotNumber ?? ''}
                    onChange={(e) => setReceiveLot({ ...receiveLot, lotNumber: e.target.value })}
                    placeholder="Printed on the box"
                    helperText={knownLot ? `Adds to lot ${knownLot.lotNumber} (${describeBalance(knownLot)} now)` : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Expiration date"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={knownLot ? knownLot.expiresOn.slice(0, 10) : receiveLot.expiresOn ?? ''}
                    onChange={(e) => setReceiveLot({ ...receiveLot, expiresOn: e.target.value })}
                    disabled={!typedLotNumber || !!knownLot}
                    required={!!typedLotNumber && !knownLot}
                    error={!!typedLotNumber && !knownLot && !receiveLot.expiresOn}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Packaging"
                    size="small"
                    fullWidth
                    value={receivePackaging.containerType}
                    onChange={(e) => setReceiveLot({ ...receiveLot, containerType: e.target.value as LotContainerType })}
                    disabled={!typedLotNumber || !!knownLot}
                  >
                    <MenuItem value="MULTI_DOSE">Multi-dose bottle</MenuItem>
                    <MenuItem value="SINGLE_DOSE">Single-dose vial</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <QuantityField
                    label="mL per container"
                    size="small"
                    fullWidth
                    value={receivePackaging.mlPerContainer ?? null}
                    onChange={(next) => setReceiveLot({ ...receiveLot, mlPerContainer: next ?? undefined })}
                    disabled={!typedLotNumber || !!knownLot}
                    required={!!typedLotNumber && !knownLot}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Manufacturer"
                    size="small"
                    fullWidth
                    value={receiveLot.manufacturer ?? knownLot?.manufacturer ?? ''}
                    onChange={(e) => setReceiveLot({ ...receiveLot, manufacturer: e.target.value })}
                    disabled={!typedLotNumber}
                  />
                </Grid>
                {typedLotNumber && !!receiveQty && !!receivePackaging.mlPerContainer && (
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {receiveQty} × {receivePackaging.mlPerContainer} mL = {Math.round(receiveQty * receivePackaging.mlPerContainer * 1000) / 1000} mL
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveItem(null)}>Cancel</Button>
          <Button variant="contained" disabled={!isReceiveValid || receiveMutation.isLoading} onClick={() => receiveMutation.mutate()}>
            Receive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer stock (Task 10, trucks) — warehouse->truck (or any
          location->location) move of one catalog-linked item. The header
          button above already gates entry (manage:inventory + online); this
          dialog assumes those checks passed. Task 11: a row's "Transfer to
          par" action opens this SAME dialog pre-filled via transferPrefill
          (To/Item/Quantity); the header button leaves it undefined, which
          preserves Task 10's plain defaults. */}
      <TransferDialog
        open={transferDialogOpen}
        onClose={handleCloseTransferDialog}
        locations={locations}
        items={items}
        defaultLocationId={defaultLocationId}
        initialToLocationId={transferPrefill?.toLocationId}
        initialCatalogItemId={transferPrefill?.catalogItemId}
        initialQuantity={transferPrefill?.quantity}
      />
    </Box>
  );
};

export default Inventory;